import { useLocation, useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { buildAuthHref, rememberAuthIntent } from '@/features/auth/auth-intent';
import { resolveAccountEmail } from '@/features/auth/account-email';
import { useAuthStore } from '@/features/auth/store';
import { fetchClubPage, resolveClubReference } from '@/features/catalog/catalog.api';
import { activeClubContentFreshnessOptions } from '@/features/catalog/catalog.query';
import { buildLegalHref } from '@/features/legal/legal-navigation';
import { useTelegramBackButton } from '@/telegram';
import { trackEvent } from '@/shared/lib/analytics';
import { trackYandexGoal } from '@/shared/lib/yandex-metrica';
import type { ClubPageChapter, ClubPageContentSummary } from '@bookclub/shared';
import { PageFrame } from '@/shared/components/ui/PageFrame';
import { NeutralLoadingState } from '@/shared/components/ui/NeutralLoadingState';
import { ErrorState } from '@/shared/components/ui/ErrorState';
import { PrimaryButton } from '@/shared/components/ui/PrimaryButton';
import { ClubHero } from '@/shared/components/club/ClubHero';
import { ClubPurchaseInfo } from '@/shared/components/club/ClubPurchaseInfo';
import { ChapterRow } from '@/shared/components/club/ChapterRow';

type ClubSource = 'catalog' | 'my-clubs';

function ClubPageBackButton({ onBack }: { onBack: () => void }) {
  return (
    <button
      onClick={onBack}
      className="flex h-10 w-10 items-center justify-center rounded-full bg-white/82 text-[var(--foreground)] shadow-[0_10px_20px_rgba(62,48,23,0.08)]"
      aria-label="Назад"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>
  );
}

function ClubPageShareButton({ onShare }: { onShare: () => void }) {
  return (
    <button
      type="button"
      onClick={onShare}
      className="flex h-10 w-10 items-center justify-center rounded-full bg-white/82 text-[var(--foreground)] shadow-[0_10px_20px_rgba(62,48,23,0.08)] transition-colors hover:bg-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(94,81,58,0.2)] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgba(247,240,227,0.92)]"
      aria-label="Поделиться клубом"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="18" cy="5" r="3" />
        <circle cx="6" cy="12" r="3" />
        <circle cx="18" cy="19" r="3" />
        <path d="M8.59 13.51 15.42 17.49" />
        <path d="M15.41 6.51 8.59 10.49" />
      </svg>
    </button>
  );
}

function FixedClubPageBackButton({ onBack }: { onBack: () => void }) {
  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      data-testid="club-page-back-button-layer"
      className="pointer-events-none fixed inset-x-0 top-[calc(var(--safe-area-top)+0.75rem)] z-40 min-[430px]:top-[calc(var(--safe-area-top)+0.875rem)]"
    >
      <div className="mx-auto w-full max-w-[var(--screen-max-width)] px-[calc(var(--page-gutter)+0.25rem)] min-[430px]:px-[calc(var(--page-gutter)+0.375rem)] min-[768px]:max-w-[var(--desktop-app-max-width)] min-[768px]:px-[var(--desktop-gutter)]">
        <div className="pointer-events-auto w-fit">
          <ClubPageBackButton onBack={onBack} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

function FixedClubPageShareButton({ onShare }: { onShare: () => void }) {
  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      data-testid="club-page-share-button-layer"
      className="pointer-events-none fixed inset-x-0 top-[calc(var(--safe-area-top)+0.75rem)] z-40 min-[430px]:top-[calc(var(--safe-area-top)+0.875rem)]"
    >
      <div className="mx-auto flex w-full max-w-[var(--screen-max-width)] justify-end px-[calc(var(--page-gutter)+0.25rem)] min-[430px]:px-[calc(var(--page-gutter)+0.375rem)] min-[768px]:max-w-[var(--desktop-app-max-width)] min-[768px]:px-[var(--desktop-gutter)]">
        <div className="pointer-events-auto">
          <ClubPageShareButton onShare={onShare} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ClubPageCopyNotification({ visible }: { visible: boolean }) {
  if (!visible || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      role="status"
      className="pointer-events-none fixed inset-x-0 top-[calc(var(--safe-area-top)+3.75rem)] z-40 flex justify-center px-[var(--page-gutter)] min-[430px]:top-[calc(var(--safe-area-top)+4rem)]"
    >
      <div className="max-w-[calc(var(--screen-max-width)-var(--page-gutter)*2)] rounded-full border border-white/60 bg-[rgba(42,35,24,0.88)] px-4 py-2 text-center text-[13px] font-semibold leading-snug text-white shadow-[0_14px_28px_rgba(42,35,24,0.16)] min-[768px]:max-w-[var(--content-readable-max-width)]">
        Ссылка на клуб скопирована
      </div>
    </div>,
    document.body,
  );
}

function FixedClubPagePrimaryCta({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  const [visualViewportBottomGap, setVisualViewportBottomGap] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const updateBottomGap = () => {
      const visualViewport = window.visualViewport;

      if (!visualViewport) {
        setVisualViewportBottomGap(0);
        return;
      }

      const layoutViewportHeight = Math.max(
        window.innerHeight || 0,
        document.documentElement.clientHeight || 0,
        visualViewport.height,
      );
      const visibleViewportBottom = visualViewport.offsetTop + visualViewport.height;
      const nextGap = Math.max(0, Math.round(layoutViewportHeight - visibleViewportBottom));

      setVisualViewportBottomGap(nextGap);
    };

    updateBottomGap();

    const visualViewport = window.visualViewport;
    window.addEventListener('resize', updateBottomGap);
    visualViewport?.addEventListener('resize', updateBottomGap);
    visualViewport?.addEventListener('scroll', updateBottomGap);

    return () => {
      window.removeEventListener('resize', updateBottomGap);
      visualViewport?.removeEventListener('resize', updateBottomGap);
      visualViewport?.removeEventListener('scroll', updateBottomGap);
    };
  }, []);

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      data-testid="club-page-primary-cta-layer"
      data-visual-viewport-bottom-gap={visualViewportBottomGap}
      className="pointer-events-none fixed inset-x-0 z-40"
      style={{ bottom: `${visualViewportBottomGap}px` }}
    >
      <div className="mx-auto w-full max-w-[var(--screen-max-width)] px-[var(--page-gutter)] pb-[calc(var(--content-safe-area-bottom)+0.75rem)] min-[430px]:pb-[calc(var(--content-safe-area-bottom)+0.875rem)] min-[768px]:max-w-[var(--content-readable-max-width)] min-[768px]:px-[var(--desktop-gutter)]">
        <div className="pointer-events-auto rounded-[28px] border border-white/55 bg-[rgba(255,251,244,0.84)] p-2.5 shadow-[0_16px_28px_rgba(62,48,23,0.08)] min-[430px]:p-3">
          <PrimaryButton
            tone="dark"
            onClick={onClick}
            className="w-full min-h-[3.5rem] text-[12px] tracking-[0.03em] min-[430px]:min-h-[3.75rem]"
          >
            {label}
          </PrimaryButton>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function formatPurchaseLabel(amount: number, currency: string) {
  if (currency.toUpperCase() === 'RUB') {
    return `Купить клуб за ${amount} ₽`;
  }

  return `Купить клуб за ${amount} ${currency}`;
}

function buildClubPagePurchaseHref(paywallSlug: string) {
  return `/paywall/${paywallSlug}?source=club-page`;
}

function buildCanonicalClubUrl(pathname: string) {
  const origin =
    typeof window !== 'undefined' && window.location.origin !== 'null' ? window.location.origin : '';

  return `${origin}${pathname}`;
}

function ClubPageContentSummaryMark({ summary }: { summary: ClubPageContentSummary }) {
  if (summary.videoMessageCount === 0) {
    return null;
  }

  return (
    <section aria-label="Сводка контента" className="px-2">
      <div className="flex flex-wrap items-center gap-2 rounded-[18px] border border-white/58 bg-[rgba(255,249,238,0.66)] px-3 py-2 shadow-[0_10px_22px_rgba(62,48,23,0.06)]">
        <span className="rounded-full bg-white/62 px-2.5 py-1 text-[12px] font-semibold leading-none text-[rgba(62,48,23,0.78)]">
          Видеосообщений: {summary.videoMessageCount}
        </span>
      </div>
    </section>
  );
}

const EMPTY_CLUB_PAGE_CONTENT_SUMMARY: ClubPageContentSummary = {
  videoMessageCount: 0,
};

async function copyTextToClipboard(text: string) {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  if (typeof document === 'undefined') {
    throw new Error('Clipboard is unavailable');
  }
  if (typeof document.execCommand !== 'function') {
    throw new Error('Fallback clipboard copy is unavailable');
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '0';
  document.body.appendChild(textarea);
  textarea.select();

  try {
    const copied = document.execCommand('copy');
    if (!copied) {
      throw new Error('Fallback clipboard copy failed');
    }
  } finally {
    textarea.remove();
  }
}

// ─── CTA logic ────────────────────────────────────────────────────────────────

function getPrimaryCta(
  accessStatus: 'owned' | 'locked',
  purchaseEntryReady: boolean,
  purchaseNeedsEmailAuth: boolean,
  price: { amount: number; currency: string } | null,
  continueItem:
    | {
        chapterId: string;
        chapterTitle: string;
        chapterIndex: number;
        lastPosition: number | null;
        updatedAt: string | null;
      }
    | null,
  chapters: ClubPageChapter[],
): {
  label: string;
  action: 'continue' | 'start_demo' | 'request_access' | 'purchase';
  chapterId?: string;
} {
  if (accessStatus === 'owned' && continueItem) {
    return { label: 'Продолжить', action: 'continue', chapterId: continueItem.chapterId };
  }
  if (accessStatus === 'owned') {
    const firstChapter = chapters[0];
    return {
      label: 'Начать читать',
      action: 'start_demo',
      chapterId: firstChapter?.id,
    };
  }
  if (purchaseEntryReady) {
    return {
      label: purchaseNeedsEmailAuth ? 'Войти по email' : price ? formatPurchaseLabel(price.amount, price.currency) : 'Купить клуб',
      action: 'purchase',
    };
  }
  const demoChapter = chapters.find((ch) => ch.access === 'demo');
  if (demoChapter) {
    return { label: 'Начать демо', action: 'start_demo', chapterId: demoChapter.id };
  }
  return { label: 'Получить доступ', action: 'request_access' };
}

// ─── ClubPage ─────────────────────────────────────────────────────────────────

export function ClubPage() {
  const { clubId } = useParams<{ clubId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const runtime = useAuthStore((s) => s.runtime);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const authUser = useAuthStore((s) => s.user);
  const clubSource: ClubSource =
    new URLSearchParams(location.search).get('source') === 'my-clubs' ? 'my-clubs' : 'catalog';
  const backTarget = clubSource === 'my-clubs' ? '/my-clubs' : '/catalog';
  const continueChapterRef = useRef<HTMLDivElement | null>(null);
  const autoScrollKeyRef = useRef<string | null>(null);
  const copyNotificationTimeoutRef = useRef<number | null>(null);
  const [copyNotificationVisible, setCopyNotificationVisible] = useState(false);

  const onBack = useCallback(() => void navigate(backTarget, { replace: true }), [backTarget, navigate]);
  useTelegramBackButton(onBack, true);
  const accountEmail = useMemo(() => resolveAccountEmail(authUser), [authUser]);
  const canonicalClubUrl = useMemo(() => buildCanonicalClubUrl(location.pathname), [location.pathname]);

  useEffect(() => {
    return () => {
      if (copyNotificationTimeoutRef.current !== null) {
        window.clearTimeout(copyNotificationTimeoutRef.current);
      }
    };
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['club-page', clubId],
    queryFn: () => fetchClubPage(clubId!),
    ...activeClubContentFreshnessOptions,
    enabled: !!clubId,
  });

  useEffect(() => {
    if (clubId) {
      trackEvent('club_open', { clubId });
      trackEvent('club_page_view', { clubId, source: 'deeplink' });
    }
  }, [clubId]);

  useEffect(() => {
    if (data?.accessStatus !== 'owned' || !clubId) {
      return;
    }

    const progressPercent = data.progress?.percentComplete ?? null;
    if (progressPercent === null || progressPercent < 80) {
      return;
    }

    trackYandexGoal(
      'club_read_80',
      {
        club_id: clubId,
        club_slug: data.club.paywallSlug ?? undefined,
        source_surface: 'club_page',
        progress_percent: progressPercent,
        milestone: 'read_80',
      },
      {
        dedupeKey: `${clubId}:read_80`,
      },
    );
  }, [clubId, data]);

  const continueItem = data?.continueItem ?? null;

  useEffect(() => {
    if (!continueItem?.chapterId) {
      autoScrollKeyRef.current = null;
      return;
    }

    const scrollKey = [
      clubId ?? 'unknown-club',
      continueItem.chapterId,
      continueItem.updatedAt ?? 'null',
      continueItem.lastPosition ?? 'null',
    ].join(':');

    if (autoScrollKeyRef.current === scrollKey || !continueChapterRef.current) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      continueChapterRef.current?.scrollIntoView({
        block: 'start',
        behavior: 'auto',
      });
      autoScrollKeyRef.current = scrollKey;
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [clubId, continueItem?.chapterId, continueItem?.lastPosition, continueItem?.updatedAt]);

  const handleShare = useCallback(() => {
    void copyTextToClipboard(canonicalClubUrl)
      .then(() => {
        if (copyNotificationTimeoutRef.current !== null) {
          window.clearTimeout(copyNotificationTimeoutRef.current);
        }

        setCopyNotificationVisible(true);
        copyNotificationTimeoutRef.current = window.setTimeout(() => {
          setCopyNotificationVisible(false);
          copyNotificationTimeoutRef.current = null;
        }, 2400);
      })
      .catch(() => undefined);
  }, [canonicalClubUrl]);

  if (isLoading) {
    return (
      <PageFrame container="adaptive-app" topInset="none" bottomInset="none" className="space-y-4 pb-6 pt-3">
        <FixedClubPageBackButton onBack={onBack} />
        <FixedClubPageShareButton onShare={handleShare} />
        <ClubPageCopyNotification visible={copyNotificationVisible} />
        <NeutralLoadingState rows={5} ariaLabel="Загрузка клуба" />
      </PageFrame>
    );
  }

  if (error || !data) {
    return (
      <PageFrame container="adaptive-app" topInset="none" bottomInset="none" className="space-y-4 pb-6 pt-3">
        <FixedClubPageBackButton onBack={onBack} />
        <FixedClubPageShareButton onShare={handleShare} />
        <ClubPageCopyNotification visible={copyNotificationVisible} />
        <ErrorState
          title="Клуб недоступен"
          description="Возможно, он был скрыт или удалён."
          onRetry={onBack}
          retryLabel="Назад"
        />
      </PageFrame>
    );
  }

  const { club, accessStatus, progress, chapters, purchase } = data;
  const clubReference = resolveClubReference(club) ?? clubId;
  const readerHref = (chapterId: string, access?: ClubPageChapter['access']) => {
    const params = new URLSearchParams({ source: clubSource });
    if (access === 'demo') {
      params.set('demo', 'reader');
    }
    return `/reader/${clubReference}/${chapterId}?${params.toString()}`;
  };
  const hasDemo = chapters.some((chapter) => chapter.access === 'demo' || chapter.isDemo);
  const paywallReference = clubReference;
  const paywallHref = `/paywall/${paywallReference}?source=${clubSource}`;
  const purchasePaywallHref = club.paywallSlug ? buildClubPagePurchaseHref(club.paywallSlug) : null;
  const requiresEmailAuthForPurchase =
    runtime === 'telegram' &&
    isAuthenticated &&
    !accountEmail &&
    accessStatus === 'locked' &&
    Boolean(club.price) &&
    Boolean(purchasePaywallHref);
  const legalHubHref = buildLegalHref('/legal', `${location.pathname}${location.search}`);
  const cta = getPrimaryCta(
    accessStatus,
    accessStatus === 'locked' && (purchase.canPurchase || requiresEmailAuthForPurchase) && Boolean(purchasePaywallHref),
    requiresEmailAuthForPurchase,
    club.price,
    continueItem,
    chapters,
  );
  const progressPercent = progress?.percentComplete ?? null;

  function handleChapterTap(chapter: ClubPageChapter) {
    trackEvent('club_chapter_click', {
      clubId,
      chapterId: chapter.id,
      access: chapter.access,
    });

    if (chapter.access === 'locked') {
      if (runtime === 'web' && !isAuthenticated) {
        const returnTo = paywallHref;
        rememberAuthIntent(returnTo);
        void navigate(buildAuthHref(returnTo));
        return;
      }
      trackEvent('locked_chapter_attempt', { clubId, chapterId: chapter.id, source: 'club_page' });
      trackEvent('paywall_open', { clubId, source: 'club_page' });
      void navigate(paywallHref);
      return;
    }
    void navigate(readerHref(chapter.id, chapter.access));
  }

  const handlePrimaryAction = () => {
    trackEvent('club_cta_click', {
      clubId,
      cta: cta.action,
      chapterId: cta.chapterId ?? null,
    });

    if (cta.action === 'request_access' || cta.action === 'purchase') {
      if (cta.action === 'purchase' && !purchasePaywallHref) {
        return;
      }

      if (cta.action === 'purchase') {
        trackYandexGoal('club_purchase_click', {
          club_id: clubId ?? club.id,
          club_slug: club.paywallSlug ?? undefined,
          source_surface: 'club_page',
          has_demo: hasDemo,
        });
      }

      const targetHref = cta.action === 'purchase' ? purchasePaywallHref! : paywallHref;

      if ((runtime === 'web' && !isAuthenticated) || (cta.action === 'purchase' && requiresEmailAuthForPurchase)) {
        const returnTo = targetHref;
        rememberAuthIntent(returnTo);
        void navigate(buildAuthHref(returnTo));
        return;
      }
      trackEvent('paywall_open', { clubId, source: 'club_page' });
      void navigate(targetHref);
      return;
    }
    if (cta.chapterId) {
      const chapter = chapters.find((item) => item.id === cta.chapterId);
      void navigate(readerHref(cta.chapterId, chapter?.access));
    }
  };

  const handleLegalHubOpen = () => {
    void navigate(legalHubHref);
  };

  return (
    <PageFrame
      container="adaptive-app"
      topInset="none"
      bottomInset="none"
      className="space-y-5 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.64),transparent_36%),linear-gradient(180deg,rgba(251,247,238,0.94),rgba(242,235,221,0.92))] pb-[calc(var(--content-safe-area-bottom)+9.25rem)] pt-1 min-[430px]:space-y-6 min-[430px]:pb-[calc(var(--content-safe-area-bottom)+9.75rem)] min-[430px]:pt-1.5"
    >
      <FixedClubPageBackButton onBack={onBack} />
      <FixedClubPageShareButton onShare={handleShare} />
      <ClubPageCopyNotification visible={copyNotificationVisible} />
      <FixedClubPagePrimaryCta label={cta.label} onClick={handlePrimaryAction} />
      <ClubHero
        title={club.title}
        bookAuthor={club.bookAuthor}
        host={club.host}
        secondHost={club.secondHost}
        showHostSubtitle={false}
        hostLayout="card"
        coverUrl={club.coverUrl}
        description={club.shortDescription}
        descriptionHeading="ЧТО БУДЕМ РАЗБИРАТЬ"
        accessStatus={accessStatus}
        progressPercent={progressPercent && progressPercent > 0 ? progressPercent : null}
        primaryActionLabel={cta.label}
        onPrimaryAction={handlePrimaryAction}
        showPrimaryAction={false}
        footer={
          accessStatus === 'locked' && club.price ? (
            <ClubPurchaseInfo price={club.price} hasDemo={hasDemo} />
          ) : null
        }
      />

      <div className="min-[768px]:mx-auto min-[768px]:max-w-[var(--content-readable-max-width)]">
        <ClubPageContentSummaryMark summary={data.contentSummary ?? EMPTY_CLUB_PAGE_CONTENT_SUMMARY} />
      </div>

      <section className="space-y-4 pt-1 min-[430px]:space-y-[1.125rem] min-[430px]:pt-2 min-[768px]:mx-auto min-[768px]:w-full min-[768px]:max-w-[var(--content-readable-max-width)]">
        <div className="px-2">
          <p className="text-[22px] font-black uppercase tracking-[-0.03em] text-[var(--foreground)] min-[430px]:text-[24px]">
            Главы
          </p>
        </div>
        <div className="rounded-[28px] border border-white/60 bg-[rgba(255,249,238,0.78)] px-4 py-4 shadow-[0_16px_32px_rgba(62,48,23,0.08)] min-[430px]:px-5 min-[430px]:py-5">
          <div className="space-y-4 min-[430px]:space-y-[1.125rem]">
            {chapters.map((chapter: ClubPageChapter, index: number) => (
              <div
                key={chapter.id}
                ref={continueItem?.chapterId === chapter.id ? continueChapterRef : null}
                className={
                  continueItem?.chapterId === chapter.id
                    ? 'scroll-mt-[calc(var(--content-safe-area-top)+0.75rem)] min-[430px]:scroll-mt-[calc(var(--content-safe-area-top)+1rem)]'
                    : ''
                }
                data-continue-chapter-target={continueItem?.chapterId === chapter.id ? 'true' : undefined}
              >
                <ChapterRow
                  chapter={chapter}
                  onTap={handleChapterTap}
                  active={continueItem?.chapterId === chapter.id}
                  layout="list"
                  showDivider={index < chapters.length - 1}
                  showIndexBubble={false}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="px-2 pb-1 pt-1 min-[430px]:pb-2 min-[768px]:mx-auto min-[768px]:w-full min-[768px]:max-w-[var(--content-readable-max-width)]">
        <button
          type="button"
          onClick={handleLegalHubOpen}
          data-testid="club-page-legal-footer"
          className="inline-flex items-center gap-1 rounded-[14px] px-1 py-1 text-left text-[12px] font-medium leading-none text-[rgba(94,81,58,0.72)] transition-colors hover:text-[rgba(94,81,58,0.88)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(94,81,58,0.2)] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgba(247,240,227,0.92)]"
        >
          <span>Inhound @ Юридическая информация</span>
        </button>
      </footer>
    </PageFrame>
  );
}
